import React from "react";
import api, { postForm } from "../../api/client";
import { useOrg } from "../../context/OrgContext";
import { useTerminology } from "../../context/TerminologyContext";
import { withOrgQS } from "../../utils/url";
import { formatDateTime } from "../../utils/datetime";
import PdfViewer from "../../components/PdfViewer";
import PageHeader from "../../components/PageHeader";
import { LotSelectionTable, ModalShell } from "../../components/ui-helpers";

export default function FailedLotsPage() {
  const { current: org } = useOrg();
  const { t } = useTerminology();
  const [rows, setRows] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [message, setMessage] = React.useState("");
  const [action, setAction] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sortBy, setSortBy] = React.useState("failed_at");
  const [sortOrder, setSortOrder] = React.useState("desc");

  const reload = React.useCallback(async () => {
    try {
      const response = await api.get("/superadmin/failed-lots");
      setRows(response.data?.items || []);
    } catch (error) {
      setRows([]);
      setMessage(error?.response?.data?.error || "Could not load failed lots.");
    }
  }, []);

  React.useEffect(() => {
    setSelected(null);
    setDetail(null);
    setRows([]);
    setMessage("");
    setSortBy("failed_at");
    setSortOrder("desc");
    let active = true;
    api.get("/superadmin/failed-lots")
      .then((response) => { if (active) setRows(response.data?.items || []); })
      .catch(() => { if (active) setMessage("Could not load failed lots."); });
    return () => { active = false; };
  }, [org]);

  React.useEffect(() => {
    if (!selected) return undefined;
    let active = true;
    setDetail(null);
    api.get("/user/get_processed_cache", {
      params: {
        vendor: selected.vendor,
        sku: selected.sku,
        lot_no: selected.lot_no,
        ...(selected.material_id ? { material_id: selected.material_id } : {}),
      },
    }).then((response) => {
      if (active) setDetail(response.data);
    }).catch(() => {
      if (active) setMessage("Material specifications could not be loaded.");
    });
    return () => { active = false; };
  }, [selected, org]);

  const pdfUrl = React.useMemo(() => {
    if (!selected) return "";
    const query = new URLSearchParams({
      vendor: selected.vendor,
      sku: selected.sku,
      lot_no: selected.lot_no,
      ...(selected.material_id ? { material_id: selected.material_id } : {}),
    });
    return withOrgQS(`${api.defaults.baseURL}/user/get_processed_pdf?${query}`, org);
  }, [selected, org]);

  const columns = React.useMemo(() => [
    {
      key: "serial",
      label: "S.No.",
      sortable: false,
      render: (_row, { absoluteIndex }) => absoluteIndex + 1,
    },
    { key: "vendor", label: t("vendor") },
    { key: "material", label: t("material") },
    { key: "sku", label: "SKU" },
    { key: "lot_no", label: t("lot") },
    {
      key: "failed_at",
      label: "Failed At",
      value: (row) => Date.parse(row.failed_at) || 0,
      render: (row) => formatDateTime(row.failed_at),
    },
    {
      key: "reason",
      label: "Reason",
      render: (row) => (
        <span title={row.reason}>
          {row.reason?.length > 100 ? `${row.reason.slice(0, 100)}…` : row.reason}
        </span>
      ),
    },
  ], [t]);

  const confirmAction = async () => {
    if (!selected || !action) return;
    setBusy(true);
    try {
      await postForm(`/superadmin/failed-lots/${action}`, { queue_id: selected.queue_id });
      setMessage(
        action === "retry"
          ? "Lot queued for another attempt."
          : "Lot marked as Failed for internal review.",
      );
      setSelected(null);
      setDetail(null);
      setAction("");
      await reload();
    } catch (error) {
      setAction("");
      setMessage(error?.response?.data?.error || "Could not update this lot. Refresh and try again.");
      await reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Super Admin"
        icon="coa"
        title="Failed Lots"
        subtitle="Resolve technical extraction failures in the selected organization."
      />
      {message ? <div className="banner info" role="status">{message}</div> : null}
      {!selected ? (
        <div className="section">
          <LotSelectionTable
            rows={rows}
            columns={columns}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
            selected={null}
            onRowClick={(row) => { setMessage(""); setSelected(row); }}
            getRowId={(row) => row.queue_id}
            searchPlaceholder="Search failed lots..."
            emptyText="No unresolved failed lots in this organization."
            pageSize={10}
          />
        </div>
      ) : (
        <div className="section">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { setSelected(null); setDetail(null); }}
          >
            Back to Failed Lots
          </button>
          <h3>{selected.vendor} · {selected.material} · {selected.sku} · {selected.lot_no}</h3>
          <div className="helper-text"><b>Technical reason:</b> {selected.reason}</div>
          <div className="two-pane" style={{ marginTop: 16 }}>
            <div className="pane pdf-pane"><PdfViewer url={pdfUrl} /></div>
            <div className="pane table-pane">
              <div className="table--card"><div className="table-scroll">
                <table className="table table--tight">
                  <thead><tr><th>Parameter</th><th>Specification</th><th>Extracted value</th></tr></thead>
                  <tbody>
                    {(detail?.specs || []).map((spec, index) => (
                      <tr key={index}>
                        <td>{spec.Parameter || "—"}</td>
                        <td>{spec.Specification || spec["Acceptance Criteria"] ||
                          ((spec["Lower Limit"] != null || spec["Upper Limit"] != null)
                            ? `${spec["Lower Limit"] ?? "—"} – ${spec["Upper Limit"] ?? "—"}` : "—")}</td>
                        <td>{detail?.results?.[index] || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {detail && !(detail.specs || []).length
                  ? <div className="helper-text">No material specifications available.</div>
                  : null}
              </div></div>
            </div>
          </div>
          <div className="card-actions">
            <button className="btn btn-secondary" type="button" onClick={() => setAction("retry")}>Retry Lot</button>
            <button className="btn btn-primary" type="button" onClick={() => setAction("confirm")}>Mark as Failed</button>
          </div>
        </div>
      )}
      <ModalShell
        open={!!action}
        title={action === "retry" ? "Retry Lot?" : "Mark as Failed?"}
        onClose={() => { if (!busy) setAction(""); }}
        actions={(
          <>
            <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => setAction("")}>Cancel</button>
            <button className="btn btn-primary" type="button" disabled={busy} onClick={confirmAction}>
              {busy ? "Working..." : action === "retry" ? "Retry Lot" : "Mark as Failed"}
            </button>
          </>
        )}
      >
        {action === "retry"
          ? "This retries the same queue row using current organization and material settings. It does not charge another QualiGate credit."
          : "Internal team members will see a generic Failed status and can request a re-upload or Manager review."}
      </ModalShell>
    </div>
  );
}
